import * as React from 'react'

import axios from 'axios'
import { uniqueId } from 'lodash-es'
import luhn from 'fast-luhn'
import moment from 'moment'
import createNormalizer from '@dooer/json-normalizer'
import createValidator from '@dooer/json-validator'

// Signup utils
const rePersonnummer = /^(\d{2})?(\d{2})(\d{2})(\d{2})([-+])?(\d{4})$/
const rePhoneNumber = /^\+[1-9][0-9]{1,14}$/

export const validateSvensktOrganisationsnummer = createValidator({
  type: 'string',
  format: 'svenskt-organisationsnummer',
})

export const normalizeSvensktOrganisationsnummer = createNormalizer({
  type: 'string',
  format: 'svenskt-organisationsnummer',
})

export function normalizeSvensktPersonnummer(input: string) {
  const match = rePersonnummer.exec(input.replace(/\s/g, ''))

  if (!match) return input

  const parts = match.slice(1)
  const currentDate = moment().format('YYYYMMDD')
  const currentCentury = currentDate.substring(0, 2)

  // eslint-disable-next-line prefer-const
  let [century = currentCentury, year, month, day, separator = '-', digits] = parts

  if (`${century}${year}${month}${day}` > currentDate) {
    century = String(Number(century) - 1)
  }

  if (separator === '+') {
    century = String(Number(century) - 1)
  }

  return `${century}${year}${month}${day}${digits}`
}

export function normalizePhoneNumber(input: string) {
  const match = rePhoneNumber.exec((input.startsWith('0') ? `+46${input.slice(1)}` : input).replace(/[\s-]/g, ''))

  if (!match) return input

  return match[0]
}

/**
 * century: matches[1]
 * date: matches[2]
 * digits: matches[3]
 * check: matches[4]
 **/
const SVENSKT_PERSONNUMMER_FORMAT = /^(\d{2})(\d{6})(\d{3})(\d{1})$/

export function validateSvensktPersonnummer(value: string): boolean {
  if (!value || !SVENSKT_PERSONNUMMER_FORMAT.test(value)) {
    return false
  }

  const century = value.substring(0, 2)
  const date = value.substring(2, 8)
  const digits = value.substring(8, 11)
  const check = value.substring(11, 12)

  return moment(`${century}${date}`, 'YYYYMMDD').isValid() && luhn(`${date}${digits}${check}`)
}

const PHONE_NUMBER_FORMAT = /^\+[1-9][0-9]{1,14}$/

export function validatePhoneNumber(value: string): boolean {
  return !!value && PHONE_NUMBER_FORMAT.test(value)
}

export const useDataApi = <TData>(url: string) => {
  const [data, setData] = React.useState<TData | undefined>()
  const [isLoading, setIsLoading] = React.useState(false)
  const [isError, setIsError] = React.useState(false)

  React.useEffect(() => {
    const fetchData = async () => {
      setIsError(false)
      setIsLoading(true)

      try {
        const result = await axios(url)

        setData(result.data)
      } catch (error) {
        setIsError(true)
      }

      setIsLoading(false)
    }

    fetchData()
  }, [url])

  return { data, isLoading, isError }
}

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const getEnv = () => {
  if (window.location.hostname.includes('pr.d-e021')) {
    return 'd-e023'
  }
  if (window.location.hostname.includes('d-e021')) {
    return window.location.hostname.split('.').slice(1, 3).join('.')
  }
  if (window.location.hostname.includes('s-e024')) {
    return 's-e024'
  }
  if (window.location.hostname.includes('b-e026')) {
    return 'b-e026'
  }
  if (window.location.hostname.includes('b-e034')) {
    return 'b-e034'
  }
  if (window.location.hostname.includes('d-e031')) {
    return 'd-e031'
  }
  if (window.location.hostname.includes('s-e032')) {
    return 's-e032'
  }
  if (
    window.location.hostname.includes('l-e033') ||
    window.location.hostname.includes('l-e025') ||
    window.location.hostname.includes('l-e001') ||
    window.location.hostname.includes('dooer')
  ) {
    return 'dooer'
  }
  return 'dooer'
}

export const onLocalOrPr = window.location.href.includes('5555') || window.location.href.includes('pr.d-e021')

export const getApiBaseUrl = () => {
  const env = getEnv()

  if (env.includes('d-e021')) {
    return `http://public-facade.${env}.com`
  }

  return `https://api.${env}.com`
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const pushVirtualPageView = (title: string, url: string, template?: 'signupV3') => {
  const templates: { [k: string]: { [k: string]: string } } = {
    signupV3: {
      title: `Signup V3 - ${capitalizeFirstLetter(title)}`,
      pageURL: `/signup-v3/${url}`,
    },
  }
  ;(window as any).dataLayer.push({
    event: 'VirtualPageView',
    virtualPageTitle: template && templates[template] ? `${templates[template].title}` : title,
    virtualPageURL: template && templates[template] ? `${templates[template].pageURL}` : url,
  })
}

const pushEvent = (event: any) => (window as any).dataLayer.push(event)
/* eslint-enable @typescript-eslint/no-explicit-any */

export type GAEventType =
  | 'order-completed-accounting'
  | 'order-saved-accounting'
  | 'order-received-accounting'
  | 'order-lead-accounting'
  | 'order-manual-accounting'
  | 'signup-accounting'
  | 'click-billing-video-play'
  | 'click-start-promo-video-play'
  | 'optimize.activate'
  | 'signup-salaries'
  | 'signup-billing'
  | 'signup'
  | 'ratsit-api-call'
  | 'newly-started-company-signup'
  | 'consultant-lead-added'
  | 'click-start-walkthrough-video-play'
  | 'sole-proprietorship-click-dropdown'

export const pushGAEvent = (event: GAEventType) => {
  pushEvent({ event })
}

export const pushCheckoutEvent = (
  step: number | null | undefined,
  products?: {
    id: string
    price: number
    quantity: number
    name?: string
    brand?: string
    category?: string
    variant?: string
  }[],
  id?: string
) => {
  let dataToPush

  if (products) {
    const getRevenue = () => {
      const prices = products.map((product) => product.price)
      return prices.reduce((a, b) => a + b, 0)
    }

    dataToPush = {
      event: 'purchase',
      ecommerce: {
        currencyCode: 'SEK',
        purchase: {
          actionField: {
            step,
            id: id || uniqueId(),
            revenue: String(getRevenue()),
            affiliation: 'Dooer',
            tax: String(getRevenue() * 0.25),
            shipping: '0',
            coupon: '',
          },
          products,
        },
      },
    }
  } else {
    dataToPush = {
      event: 'checkout',
      ecommerce: {
        currencyCode: 'SEK',
        checkout: {
          actionField: { step },
        },
      },
    }
  }

  pushEvent(dataToPush)
}

export const companyIsAB = (organizationNumber: string) => {
  const normalized = normalizeSvensktOrganisationsnummer(organizationNumber)
  const splitted = normalized.split('')

  return splitted[0] === '1' && splitted[1] === '6' && splitted[2] === '5'
}

export const storageAvailable = (type: 'localStorage' | 'sessionStorage') => {
  let storage
  try {
    storage = window[type]
    const x = '__storage_test__'
    storage.setItem(x, x)
    storage.removeItem(x)
    return true
  } catch (e) {
    return (
      e instanceof window.DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    )
  }
}

export const scroll = (ref: React.RefObject<HTMLDivElement>, block?: 'center' | 'start' | 'end') => {
  if (ref && ref.current) {
    ref.current.scrollIntoView({ block: block || 'center', behavior: 'smooth' })
  }
}

export const commonKeyValues = {
  enter: 13,
  left: 37,
  right: 39,
  up: 38,
  down: 40,
  escape: 27,
}

export type HTMLEvent = React.ChangeEvent<HTMLFormElement>

export const termsAndConditionsLink = 'https://dooer.com/tos'
export const assignmentLetterLink = '/Uppdragsbrev_Dooer.pdf'

export function useLocalStorageState<TType>(storageKey: string, defaultValue: TType): [TType, (value: TType) => void] {
  const initialDefaultValue =
    (storageAvailable('localStorage') ? JSON.parse(window.localStorage.getItem(storageKey) || 'null') : null) ??
    defaultValue

  const [value, setValue] = React.useState<TType>(initialDefaultValue)

  return [
    value,
    (nextValue: TType) => {
      if (storageAvailable('localStorage')) {
        window.localStorage.setItem(storageKey, JSON.stringify(nextValue))
      }

      setValue(nextValue)
    },
  ]
}
